<template>
  <div v-if="canEditSignedApplication">
    <div v-show="personalInfo" class="transition duration-500 ease-in-out">
      <lf-h4 class="pb-2 pl-2">
        {{
          !!docLength
            ? $t("DEALS.DEAL_PROGRESS.SIGNED.RESIGN_LINK")
            : $t("DEALS.DEAL_PROGRESS.SIGNED.SIGN_LINK")
        }}
      </lf-h4>
      <div class="flex items-center justify-between space-x-2">
        <widget-link-field
          class="pl-3"
          :placeholder="$t('COMMON.CLICK_TO_GENERATE')"
          :link="tempDocusignLink"
        />
        <lf-composite-dropdown
          v-model="selectedContractTemplateOption"
          class="h-11-5 min-w-63"
          dropdown-class-names="z-20"
          placeholder="Consent Template"
          data-cy="contract-template-dropdown"
          :option-sources="[
            {
              title: 'Consent Templates',
              id: 'consentTemplate',
              options: (params: GetConsentTemplatesParams) =>
                consentTemplateService.getAllTemplates({
                  client_id: client?.id,
                  ...params,
                }),
            },
            {
              title: 'Contract Templates',
              id: 'docusignTemplate',
              options: templateDropdownOptions,
            },
          ]"
          show-tooltip
        >
          <template #option="{ option, key }">
            <div class="flex items-center space-x-2">
              <span>{{ option.name || option }}</span>

              <icon-lendflow
                v-if="isLendflowContractTemplate(key, option)"
                v-tooltip="`${$t('COMMON.LENDFLOW')} ${$t('COMMON.TEMPLATE')}`"
                class="size-4 text-green-500"
              />
            </div>
          </template>
        </lf-composite-dropdown>
        <lf-link class="whitespace-nowrap" @click="generateNewLink">
          {{ $t("DEALS.DEAL_PROGRESS.SIGNED.GENERATE_NEW_LINK") }}
        </lf-link>
      </div>
    </div>
  </div>
  <div
    v-if="!!docLength && !showList"
    class="relative flex flex-1 space-y-4 sm:px-0 md:px-10 xl:px-50"
  >
    <button
      v-if="docLength > 1"
      class="absolute top-1/2 left-0 rounded-sm px-2 hover:bg-gray-100"
      @click.prevent="handlePrevDoc"
    >
      <icon-base
        :icon="IconChevronLeft"
        height="20"
        width="10"
        viewBox="0 0 16 27"
      />
    </button>
    <div
      class="mt-5 flex flex-1 flex-col"
      :class="{ 'h-85-screen': !!docLength }"
    >
      <div v-if="currentDocInfo" class="mb-6 flex justify-between">
        <div>
          <documents-list-item-name :model="currentDocInfo" />
          <span class="ml-10">
            {{ $t("COMMON.APPLICATION") }} &bull;
            {{ formatDateCustom(currentDocInfo?.created_at, "MM/dd/yyyy") }}
          </span>
        </div>
        <icon-base
          class="cursor-pointer"
          :icon="IconDownload"
          @click.prevent="download(currentDocInfo)"
        />
      </div>
      <div class="z-0 overflow-auto">
        <show-file v-if="!!docLength" :file-url="currentDocInfo?.file_url" />
      </div>
    </div>
    <button
      v-if="docLength > 1"
      class="absolute top-1/2 right-0 rounded-sm px-2 py-4 hover:bg-gray-100"
      @click.prevent="handleNextDoc"
    >
      <icon-base
        class="rotate-180 transform"
        :icon="IconChevronLeft"
        height="20"
        width="10"
        viewBox="0 0 16 27"
      />
    </button>
  </div>
  <div v-if="!!docLength && showList">
    <div class="document-list p-4 font-semibold text-gray-500">
      <span>{{ $t("COMMON.NAME") }}</span>
      <span>{{ $t("COMMON.OWNER") }}</span>
      <span>{{ $t("DEALS.DOCUMENTS.DOCUMENT_TYPE") }}</span>
      <span>{{ $t("COMMON.DATE") }}</span>
      <span>{{ $t("DEALS.DOCUMENTS.TABLE.SIZE") }}</span>
    </div>
    <div
      v-for="(doc, index) in signedDocs"
      :key="doc.id"
      class="document-list mb-3 rounded-sm border p-4"
    >
      <documents-list-item-name :model="doc" />
      <span>
        {{ doc.personal_information?.first_name }}
        {{ doc.personal_information?.last_name }}
      </span>
      <span>{{ $t("DEALS.DOCUMENTS.APPLICATION") }}</span>
      <span>{{ formatDateCustom(doc.created_at, "MM/dd/yyyy") }}</span>
      <span>{{ formatDocumentListFileSize(doc.file_size) }}</span>
      <div v-on-click-outside="() => setShowDropdown(null)" class="relative">
        <icon-base
          class="cursor-pointer"
          :icon="IconMore"
          height="24"
          width="24"
          data-cy="menu-icon"
          @click.prevent="setShowDropdown(index)"
        />
        <div
          v-if="showDropdown === index"
          class="absolute right-5 bottom-0 z-100 translate-y-full transform overflow-hidden rounded-sm bg-white text-left shadow-lg"
        >
          <button
            class="block min-w-40 cursor-pointer p-2 text-left text-gray-600 hover:bg-gray-100"
            @click.prevent.stop="download(doc)"
          >
            {{ $t("COMMON.DOWNLOAD") }}
          </button>
          <button
            v-if="canEditDealApplicationAndUnderwriting"
            class="block min-w-40 cursor-pointer p-2 text-left text-red-500 hover:bg-gray-100"
            data-cy="delete-button"
            @click.prevent.stop="deleteDocId = doc.id"
          >
            {{ $t("COMMON.DELETE") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <confirm-modal
    v-if="deleteDocId !== undefined"
    :description="$t('DEALS.DOCUMENTS.DELETE_CONFIRMATION')"
    confirm-button-type="error"
    :confirm-button="$t('COMMON.DELETE')"
    :close="() => (deleteDocId = undefined)"
    @confirm="handleDeleteFile()"
  />
  <signed-deal-empty-state
    v-if="showDocumentEmptyState"
    :no-signed-apps="!allSignedDocs.length"
    :no-signed-apps-for-product-type="
      !signedDocs.length && !!allSignedDocs.length && !!productType
    "
    :product-type="productType"
    :open-modal="openModal"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import { IconLendflow } from "@lendflow/icons";

import { APPLICATION_SOURCE, SIGNED_DOC_FILE_TYPE } from "@/helpers/constants";
import { downloadFile } from "@/helpers/files";
import {
  formatDateCustom,
  formatDocumentListFileSize,
} from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { useContractBuilder } from "@/hooks/contractBuilder";
import { useDealFiles, useDeals } from "@/hooks/deals";
import applicationsApiService from "@/services/modules/applications";
import consentTemplateService, {
  type GetConsentTemplatesParams,
} from "@/services/modules/consentTemplate";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import SignedDealEmptyState from "./SignedDealEmptyState.vue";
import WidgetLinkField from "./WidgetLinkField.vue";
import ShowFile from "@/components/ShowFile.vue";
import DocumentsListItemName from "@/components/deals/documents/DocumentsListItemName.vue";
import IconDownload from "@/components/icons/IconDownload.vue";
import IconMore from "@/components/icons/IconMore.vue";
import IconChevronLeft from "@/components/icons/deals/IconChevronLeft.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfCompositeDropdown from "@/components/ui/inputs/LfCompositeDropdown.vue";

import type { CompositeDropdownModel, IFile } from "@/models/common";
import type { ProductType } from "@/models/options";
import type { IOwner } from "@/models/users";
import type { PropType } from "vue";

const props = defineProps({
  personalInfo: {
    type: Object as PropType<IOwner | null>,
    default: null,
  },
  productType: {
    type: Number as PropType<ProductType | null>,
    default: undefined,
  },
  showList: {
    type: Boolean,
    required: true,
  },
  openModal: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const { dispatch, commit } = useStore();
const { isFunder } = useAuth();
const {
  activeDeal,
  canEditDealApplicationAndUnderwriting,
  canEditDealProgress,
  client,
} = useDeals();
const { files } = useDealFiles();
const { templateDropdownOptions, isLendflowContractTemplate } =
  useContractBuilder();
const contractsStore = useContractBuilderStore();
const { getContractTemplates } = contractsStore;

const tempDocusignLink = ref("");
const currentDoc = ref(0);
const showDropdown = ref<number | null>(null);
const deleteDocId = ref<number | undefined>(undefined);
const selectedContractTemplateOption = ref<CompositeDropdownModel>({
  id: "",
  label: "",
  source: "",
});
const docLength = computed(() => signedDocs.value?.length ?? 0);
const showDocumentEmptyState = computed(
  () =>
    (!signedDocs.value.length &&
      !!allSignedDocs.value.length &&
      !!props.productType) ||
    !allSignedDocs.value.length
);

const signedDocs = computed(
  () =>
    allSignedDocs.value?.filter((doc) =>
      props.productType ? doc.product_id === Number(props.productType) : true
    ) || []
);

const allSignedDocs = computed(
  () =>
    files.value.filter((file) => {
      if (!props.personalInfo?.id) {
        return file.file_type === SIGNED_DOC_FILE_TYPE;
      }
      return (
        file.file_type === SIGNED_DOC_FILE_TYPE &&
        file.personal_information?.id === props.personalInfo.id
      );
    }) || []
);

const currentDocInfo = computed(() => signedDocs.value?.[currentDoc.value]);

const canEditSignedApplication = computed(
  () =>
    !isFunder &&
    (canEditDealApplicationAndUnderwriting.value || canEditDealProgress.value)
);

watch(
  () => props.personalInfo,
  () => {
    currentDoc.value = 0;
    tempDocusignLink.value = "";
  }
);

watch(
  () => props.productType,
  () => {
    currentDoc.value = 0;
    tempDocusignLink.value = "";
  }
);

const setShowDropdown = (index: number | null) => {
  if (showDropdown.value === index) {
    return (showDropdown.value = null);
  }
  showDropdown.value = index;
};

const download = (file: IFile | undefined) => {
  if (!file) {
    return;
  }
  downloadFile(file.file_url, {}, file.original_name);
};

const handlePrevDoc = () => {
  const prevDoc =
    currentDoc.value === 0 ? docLength.value - 1 : currentDoc.value - 1;
  currentDoc.value = prevDoc;
};

const handleNextDoc = () => {
  const nextDoc =
    currentDoc.value === docLength.value - 1 ? 0 : currentDoc.value + 1;
  currentDoc.value = nextDoc;
};

const handleDeleteFile = () => {
  if (deleteDocId.value === undefined) {
    return;
  }
  dispatch("applications/deleteFile", { id: deleteDocId.value });
  showDropdown.value = null;
};

const generateNewLink = async (showMessage = false) => {
  if (!props.personalInfo) {
    return;
  }
  const payload: Record<string, unknown> = {
    owner: props.personalInfo.id,
  };
  if (selectedContractTemplateOption.value.id) {
    const key =
      selectedContractTemplateOption.value.source === "docusignTemplate"
        ? "contract_template_id"
        : "consent_template_id";
    payload[key] = selectedContractTemplateOption.value.id;
  }
  const response = await dispatch("applications/generateDocuSignLink", payload);
  tempDocusignLink.value = response;
  if (showMessage) {
    commit("setGlobalMessage", {
      title: "New link generated",
      type: "success",
    });
  }
};

onMounted(async () => {
  getContractTemplates({ clients: client.value?.id ? [client.value.id] : [] });
  if (activeDeal.value.source !== APPLICATION_SOURCE.WIDGET) {
    return;
  }
  const widgetInfo = await applicationsApiService.getWidgetInfo(
    activeDeal.value.id
  );

  if (widgetInfo.consent_template) {
    selectedContractTemplateOption.value = {
      id: widgetInfo.consent_template.id,
      label: widgetInfo.consent_template.name,
      source: "consentTemplate",
    };
  } else if (widgetInfo.contract_template) {
    selectedContractTemplateOption.value = {
      id: widgetInfo.contract_template.id,
      label: widgetInfo.contract_template.name,
      source: "docusignTemplate",
    };
  } else {
    const { data: consentTemplates } =
      await consentTemplateService.getAllTemplates({
        client_id: client.value?.id,
      });
    if (!consentTemplates.length) {
      return;
    }
    const { name, id } = consentTemplates[0];
    selectedContractTemplateOption.value = {
      id,
      label: name,
      source: "consentTemplate",
    };
  }
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.document-list {
  display: grid;
  grid-template-columns: 43% 13% 16% 15% 8% 5%;
}
</style>
